import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import CompanyLogo from '../../components/Common/CompanyLogo';
import ArticleImage, { ArticleImageData } from '../../components/Common/ArticleImage';
import { ConfirmModal } from '../../components/Common/ConfirmModal';

interface ProposedArticle {
    id: number;
    societe_symbole: string;
    exercice: number;
    period_type: string;
    titre: string;
    content_html: string;
    model_used?: string | null;
    statut: string;
    created_at?: string | null;
    published_at?: string | null;
    email_envoye?: boolean;
    email_envoye_at?: string | null;
    email_destinataires_count?: number | null;
    image?: ArticleImageData | null;
}

const STATUT_STYLES: Record<string, { bg: string; fg: string; border: string; label: string }> = {
    PROPOSE: { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd', label: 'Proposé' },
    PUBLIE: { bg: '#d1fae5', fg: '#065f46', border: '#6ee7b7', label: 'Publié' },
    REJETE: { bg: '#f1f5f9', fg: '#64748b', border: '#cbd5e1', label: 'Rejeté' },
};

const StatutBadge: React.FC<{ statut: string }> = ({ statut }) => {
    const style = STATUT_STYLES[statut] || STATUT_STYLES.PROPOSE;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '11px',
            fontWeight: 600, letterSpacing: '0.04em', backgroundColor: style.bg, color: style.fg,
            border: `1px solid ${style.border}`,
        }}>
            {style.label}
        </span>
    );
};

const inputStyle: React.CSSProperties = {
    padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px',
    backgroundColor: 'var(--bg-input, white)',
};

const AdminProposedArticlesScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [articles, setArticles] = useState<ProposedArticle[]>([]);
    const [loading, setLoading] = useState(false);
    const [statutFilter, setStatutFilter] = useState('PROPOSE');
    const [societeFilter, setSocieteFilter] = useState('');

    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [editTitre, setEditTitre] = useState('');
    const [editContentHtml, setEditContentHtml] = useState('');
    const [savingId, setSavingId] = useState<number | null>(null);
    const [actionMessage, setActionMessage] = useState<Record<number, string>>({});
    const [envoiEnAttente, setEnvoiEnAttente] = useState<ProposedArticle | null>(null);

    const fetchArticles = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statutFilter) params.set('statut', statutFilter);
            if (societeFilter) params.set('societe_symbole', societeFilter);
            const qs = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`${API_URL}/v1/admin/proposed-articles${qs}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setArticles(Array.isArray(data) ? data : []);
            } else {
                setArticles([]);
            }
        } catch (error) {
            console.error('Error fetching proposed articles:', error);
            setArticles([]);
        } finally {
            setLoading(false);
        }
    }, [token, statutFilter, societeFilter]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchArticles();
        }
    }, [token, hasPermission, fetchArticles]);

    const handleExpand = (article: ProposedArticle) => {
        if (expandedId === article.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(article.id);
        setEditTitre(article.titre);
        setEditContentHtml(article.content_html);
        setActionMessage(prev => ({ ...prev, [article.id]: '' }));
    };

    const handleSave = async (articleId: number) => {
        setSavingId(articleId);
        try {
            const response = await fetch(`${API_URL}/v1/admin/proposed-articles/${articleId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ titre: editTitre, content_html: editContentHtml }),
            });
            if (response.ok) {
                setActionMessage(prev => ({ ...prev, [articleId]: '✅ Modifications enregistrées.' }));
                fetchArticles();
            } else {
                setActionMessage(prev => ({ ...prev, [articleId]: 'Erreur lors de la sauvegarde.' }));
            }
        } catch (error) {
            setActionMessage(prev => ({ ...prev, [articleId]: 'Erreur réseau.' }));
        } finally {
            setSavingId(null);
        }
    };

    const handleAction = async (articleId: number, action: 'publish' | 'reject' | 'send-email') => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/proposed-articles/${articleId}/${action}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                if (action === 'send-email') {
                    setActionMessage(prev => ({ ...prev, [articleId]: `✅ Envoyé à ${data.sent_count ?? 0}/${data.total_candidates ?? 0} abonné(s) Pro.` }));
                } else {
                    setActionMessage(prev => ({ ...prev, [articleId]: action === 'publish' ? '✅ Publié.' : '✅ Rejeté.' }));
                }
                fetchArticles();
            } else {
                const libelle = { publish: 'la publication', reject: 'le rejet', 'send-email': "l'envoi par e-mail" }[action];
                setActionMessage(prev => ({ ...prev, [articleId]: `Échec de ${libelle} (erreur ${response.status}). Réessayez ou consultez les journaux de l'API.` }));
            }
        } catch (error) {
            setActionMessage(prev => ({ ...prev, [articleId]: 'Erreur réseau.' }));
        }
    };

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé.</p></Box>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon='smart_toy' title='Articles Proposés' />

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Articles générés automatiquement par l'IA à chaque import de rapport financier</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                        Aucun article n'est publié ni envoyé automatiquement. Relisez/corrigez le HTML si besoin,
                        puis choisissez indépendamment de le publier (page Publications, visible par tous les
                        utilisateurs connectés) et/ou de l'envoyer par email à tous les abonnés Pro.
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <select value={statutFilter} onChange={e => setStatutFilter(e.target.value)} style={inputStyle}>
                            <option value="">Tous les statuts</option>
                            <option value="PROPOSE">Proposé</option>
                            <option value="PUBLIE">Publié</option>
                            <option value="REJETE">Rejeté</option>
                        </select>
                        <input
                            type="text" placeholder="Filtrer par symbole (ex: STBC)" value={societeFilter}
                            onChange={e => setSocieteFilter(e.target.value.toUpperCase())} style={inputStyle}
                        />
                        <button
                            onClick={() => fetchArticles()} disabled={loading}
                            style={{ backgroundColor: 'transparent', color: '#4f46e5', border: '1px solid #4f46e5', padding: '9px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            🔄 Actualiser
                        </button>
                    </div>

                    {loading ? (
                        <p>Chargement...</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Créé le</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Société</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Période</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Titre</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Statut</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {articles.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                Aucun article pour ce filtre.
                                            </td>
                                        </tr>
                                    ) : articles.map((a, idx) => (
                                        <React.Fragment key={a.id}>
                                            <tr style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{a.created_at ? a.created_at.slice(0, 10) : '—'}</td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                        <CompanyLogo symbol={a.societe_symbole} size={24} />
                                                        {a.societe_symbole}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{a.period_type} {a.exercice}</td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                                                    <span onClick={() => handleExpand(a)} style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                                                        {a.titre}
                                                    </span>
                                                    {a.email_envoye && (
                                                        <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 700, color: '#0369a1' }}>
                                                            ✉️ Envoyé ({a.email_destinataires_count ?? 0})
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <StatutBadge statut={a.statut} />
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        onClick={() => handleExpand(a)}
                                                        style={{ backgroundColor: 'transparent', color: '#4f46e5', border: '1px solid #4f46e5', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '8px' }}
                                                    >
                                                        {expandedId === a.id ? 'Fermer' : 'Voir / Éditer'}
                                                    </button>
                                                    {a.statut !== 'PUBLIE' && (
                                                        <button
                                                            onClick={() => handleAction(a.id, 'publish')}
                                                            style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '8px' }}
                                                        >
                                                            Publier
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => setEnvoiEnAttente(a)}
                                                        style={{ backgroundColor: '#0ea5e9', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '8px' }}
                                                    >
                                                        Envoyer par email
                                                    </button>
                                                    {a.statut !== 'REJETE' && (
                                                        <button
                                                            onClick={() => handleAction(a.id, 'reject')}
                                                            style={{ backgroundColor: '#64748b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
                                                        >
                                                            Rejeter
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {expandedId === a.id && (
                                                <tr>
                                                    <td colSpan={6} style={{ padding: '16px', backgroundColor: 'var(--bg-hover)' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                            <div>
                                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Titre</label>
                                                                <input
                                                                    type="text" value={editTitre} onChange={e => setEditTitre(e.target.value)}
                                                                    style={{ ...inputStyle, width: '100%', marginBottom: '10px', boxSizing: 'border-box' }}
                                                                />
                                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Contenu HTML</label>
                                                                <textarea
                                                                    value={editContentHtml} onChange={e => setEditContentHtml(e.target.value)}
                                                                    rows={12}
                                                                    style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', fontSize: '12px', boxSizing: 'border-box' }}
                                                                />
                                                                <button
                                                                    onClick={() => handleSave(a.id)}
                                                                    disabled={savingId === a.id}
                                                                    className='button button-purple'
                                                                    style={{ marginTop: '10px', padding: '8px 16px' }}
                                                                >
                                                                    {savingId === a.id ? 'Enregistrement...' : 'Enregistrer les modifications'}
                                                                </button>
                                                            </div>
                                                            <div>
                                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Aperçu</label>
                                                                <div
                                                                    style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '16px', backgroundColor: 'var(--bg-input, white)', maxHeight: '400px', overflowY: 'auto' }}
                                                                >
                                                                    <CompanyLogo symbol={a.societe_symbole} size={40} />
                                                                    <ArticleImage image={a.image} />
                                                                    <div dangerouslySetInnerHTML={{ __html: `<h3>${editTitre}</h3>${editContentHtml}` }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {actionMessage[a.id] && (
                                                            <p style={{ marginTop: '12px', fontSize: '13px', color: '#475569' }}>{actionMessage[a.id]}</p>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>
            <ConfirmModal
                isOpen={envoiEnAttente !== null}
                title="Envoyer l'article par e-mail"
                message={envoiEnAttente
                    ? `Envoie réellement « ${envoiEnAttente.titre} » à tous les abonnés Pro actifs, dans son état actuel. Un e-mail parti ne peut pas être rappelé.`
                    : ''}
                confirmLabel='Envoyer'
                isDestructive
                onConfirm={() => {
                    const article = envoiEnAttente;
                    setEnvoiEnAttente(null);
                    if (article) handleAction(article.id, 'send-email');
                }}
                onCancel={() => setEnvoiEnAttente(null)}
            />
        </SiteLayout>
    );
};

export default AdminProposedArticlesScreen;
